import React, { useEffect, useState, useRef } from 'react';
import { proFetch, proHeaders } from '../../utils/proApi';
import { BASE_DEPOT, lireJson, deposerDirect, suivreTache, plageDe, duree } from '../../utils/deviseur';

// Le dossier de visite rattaché à un devis : les dictées à réécouter, les
// photos, ce que l'IA y a vu, la transcription et la proposition chiffrée.
// Tout ce que le Deviseur a produit, retrouvable depuis la page du devis au
// lieu de disparaître avec l'onglet où l'analyse a tourné.

// Trois lignes à l'écran, le texte complet au toucher (un téléphone n'a pas
// de survol). Une seule ligne tronquée cachait l'essentiel des constats.
function Tuile({ titre, texte, ton = 'neutre' }) {
  const [ouvert, setOuvert] = useState(false);
  const fond = ton === 'attention' ? 'bg-amber-50 border-amber-200' : ton === 'constat' ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200';
  return (
    <button type="button" title={texte} onClick={() => setOuvert(o => !o)}
            className={`text-left rounded-lg border px-3 py-2 self-start ${fond} hover:border-orange-300 transition-colors`}>
      {titre && <span className="block text-xs font-semibold text-slate-800">{titre}</span>}
      <span className={`block text-xs text-slate-600 ${ouvert ? '' : 'line-clamp-3'}`}>{texte}</span>
    </button>
  );
}

const eur = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' €';
const mo = (n) => n ? `${(n / 1048576).toFixed(1)} Mo` : '';
const jour = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '';

// « reference » et non « ref » : ref est réservé par React (référence DOM) — en
// prod, `ref="APX-…"` sur un composant fonction faisait tomber toute la page.
// Les blocs par écran du devisage : « ressources » (à réécouter, à revoir,
// à compléter) et « constats » (ce qu'on en a tiré). Sans section, tout.
const SECTIONS = {
  ressources: ['dictees', 'photos', 'documents', 'transcription', 'completer'],
  constats: ['entete', 'observations', 'constats'],
};

// Un point « à vérifier » auquel on répond : un clic ouvre la réponse, elle est
// conservée sur le dossier et l'analyse relancée la lit avant la dictée.
function Question({ texte, reponse, onRepondre }) {
  const [ouvert, setOuvert] = useState(false);
  const [brouillon, setBrouillon] = useState(reponse || '');
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { setBrouillon(reponse || ''); }, [reponse]);
  const enregistrer = async (valeur) => {
    setEnvoi(true);
    try { await onRepondre(valeur); setOuvert(false); } finally { setEnvoi(false); }
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${reponse ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
      <button type="button" onClick={() => setOuvert(o => !o)} className="w-full text-left" title={reponse ? 'Modifier la réponse' : 'Répondre'}>
        <span className={`block text-xs text-slate-700 ${ouvert ? '' : 'line-clamp-3'}`}>{texte}</span>
        {reponse && !ouvert && <span className="block text-xs font-semibold text-emerald-800 mt-1">✓ {reponse}</span>}
        {!reponse && !ouvert && <span className="block text-[11px] text-amber-700 mt-0.5">Cliquer pour répondre</span>}
      </button>
      {ouvert && (
        <div className="mt-2">
          <textarea value={brouillon} onChange={e => setBrouillon(e.target.value)} rows={2} autoFocus
                    placeholder="Votre réponse — elle fera foi à la prochaine analyse"
                    className="w-full text-xs rounded-md border border-slate-300 px-2 py-1.5 outline-none focus:border-orange-400" />
          <div className="flex items-center gap-2 mt-1.5">
            <button type="button" disabled={envoi || !brouillon.trim()} onClick={() => enregistrer(brouillon.trim())}
                    className="px-3 py-1 rounded-md bg-slate-900 text-white text-xs font-bold disabled:opacity-40">Enregistrer</button>
            {reponse && <button type="button" disabled={envoi} onClick={() => enregistrer('')} className="px-2 py-1 text-xs font-semibold text-red-600">Effacer</button>}
            <button type="button" onClick={() => { setOuvert(false); setBrouillon(reponse || ''); }} className="px-2 py-1 text-xs text-slate-500">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DossierVisite({ reference, section, onReanalyse }) {
  const montrer = (bloc) => !section || SECTIONS[section].includes(bloc);
  const [dossier, setDossier] = useState(null);
  const [etat, setEtat] = useState('chargement');
  const [voirTranscription, setVoirTranscription] = useState(false);
  // Compléter le dossier : des pièces de plus, conservées telles quelles ou
  // avec relance de l'analyse sur l'ensemble.
  const [ajouts, setAjouts] = useState([]);
  const [reanalyser, setReanalyser] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [etape, setEtape] = useState('');
  const [pct, setPct] = useState(0);
  const [debut, setDebut] = useState(0);
  const [ecoule, setEcoule] = useState(0);
  const [message, setMessage] = useState('');
  const [spot, setSpot] = useState(null);   // index de la photo en pleine lumière
  const inputRef = useRef(null);

  // Le projecteur se ferme à Échap, se déplace aux flèches.
  useEffect(() => {
    if (spot === null) return undefined;
    const k = (e) => {
      if (e.key === 'Escape') setSpot(null);
      if (e.key === 'ArrowRight') setSpot(i => Math.min((dossier?.photos?.length || 1) - 1, i + 1));
      if (e.key === 'ArrowLeft') setSpot(i => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [spot, dossier]);

  const charger = () => {
    if (!reference) return Promise.resolve();
    return proFetch(`/releve/dossier/${reference}`)
      .then(async r => {
        if (r.status === 404) { setEtat('aucun'); return; }
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Lecture impossible');
        setDossier(d); setEtat('ok');
      })
      .catch(() => setEtat('erreur'));
  };
  useEffect(() => { charger(); }, [reference]); // eslint-disable-line react-hooks/exhaustive-deps

  // Répondre à un point à vérifier, puis relancer l'analyse avec les réponses.
  const repondre = async (question, reponse) => {
    const r = await proFetch(`/releve/dossier/${reference}/reponses`, { method: 'POST', body: JSON.stringify({ question, reponse }) });
    const rep = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(rep.error || 'Réponse non enregistrée');
    setDossier(d => ({ ...d, reponses: rep.reponses || [] }));
  };
  const relancerAvecReponses = async () => {
    setEnvoi(true); setMessage(''); setEtape('Préparation'); setPct(0); setEcoule(0); setDebut(Date.now());
    try {
      const r = await proFetch(`/releve/dossier/${reference}/ajouter`, { method: 'POST', body: JSON.stringify({ fichiers: [], reanalyser: true }) });
      const rep = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(rep.error || `Relance refusée (${r.status})`);
      await suivreTache(rep.tache, proHeaders(), setEtape);
      setPct(100);
      if (onReanalyse) onReanalyse(); else await charger();
    } catch (e) { setMessage(e.message); }
    finally { setEnvoi(false); setEtape(''); }
  };

  useEffect(() => {
    if (!envoi) return undefined;
    const { debut: d0, exact } = plageDe(etape);
    setPct(p => Math.max(p, exact ?? d0));
    const t = setInterval(() => {
      const { fin } = plageDe(etape);
      setPct(p => (p < fin - 1 ? p + Math.max(0.15, (fin - 1 - p) * 0.04) : p));
      setEcoule(Math.round((Date.now() - debut) / 1000));
    }, 500);
    return () => clearInterval(t);
  }, [envoi, etape, debut]);

  const ajouter = async () => {
    if (!ajouts.length && !reanalyser) return;
    setEnvoi(true); setMessage(''); setEtape('Envoi des fichiers'); setPct(0); setEcoule(0); setDebut(Date.now());
    try {
      const url = `${BASE_DEPOT}/api/pro/releve/dossier/${reference}/ajouter`;
      let r;
      const cles = await deposerDirect(ajouts, proHeaders(), setEtape);
      if (cles || (BASE_DEPOT && !ajouts.length)) {
        r = await fetch(url, { method: 'POST', headers: proHeaders(), body: JSON.stringify({ fichiers: cles || [], reanalyser }) });
      } else {
        const form = new FormData();
        ajouts.forEach(f => form.append('fichiers', f));
        form.append('reanalyser', String(reanalyser));
        const { 'Content-Type': _ct, ...entetes } = proHeaders();
        r = await fetch(url, { method: 'POST', headers: entetes, body: form });
      }
      const rep = await lireJson(r);
      if (!r.ok) throw new Error(rep.error || `Ajout refusé (${r.status})`);
      if (rep.tache) {
        setEtape('Préparation');
        await suivreTache(rep.tache, proHeaders(), setEtape);
        setPct(100);
        setMessage('Dossier réanalysé avec l’ensemble des pièces.');
      } else {
        setMessage(`Pièces conservées — ${rep.dictees} dictée${rep.dictees > 1 ? 's' : ''}, ${rep.photos} photo${rep.photos > 1 ? 's' : ''}, ${rep.documents} document${rep.documents > 1 ? 's' : ''} au dossier.`);
      }
      setAjouts([]);
      await charger();
    } catch (e) { setMessage(e.message); }
    finally { setEnvoi(false); setEtape(''); }
  };

  if (etat === 'chargement' || etat === 'aucun') return null;
  if (etat === 'erreur') return <p className="text-xs text-slate-400 mb-3">Dossier de visite illisible pour le moment.</p>;

  const d = dossier;
  const observations = (d.observationsPhotos || []).filter(o => o.constats?.length);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 mb-3 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h2 className="text-sm font-semibold text-slate-700">
          Dossier de visite
          <span className="font-normal text-slate-400"> — analysé le {jour(d.analyseLe)}</span>
        </h2>
      </div>

      {montrer('dictees') && d.dictees?.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-2 mb-4">
          {d.dictees.map((x, i) => (
            <div key={i} className="min-w-0 rounded-lg border border-slate-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm text-slate-700 min-w-0 truncate">{x.nom} <span className="text-slate-400 text-xs">{mo(x.taille)}</span></span>
                {x.url && /^video\//.test(x.type) && (
                  <a href={x.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-orange-700 hover:underline shrink-0">Vidéo</a>
                )}
              </div>
              {/* Une dictée s'écoute : lecteur audio pour tout le monde — un mp4 sans
                  image affichait un grand cadre noir, une vidéo WhatsApp en portrait
                  prenait tout l'écran. La vidéo reste à un clic. */}
              {x.url
                ? <audio controls preload="metadata" src={x.url} className="w-full h-10" />
                : <span className="text-xs text-slate-400">lien indisponible</span>}
            </div>
          ))}
        </div>
      )}

      {montrer('photos') && d.photos?.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5 mb-4">
          {d.photos.map((p, i) => (
            <button key={i} type="button" onClick={() => setSpot(i)} title={p.nom}
                    className="block h-14 sm:h-16 w-full overflow-hidden rounded-md bg-slate-100 hover:ring-2 hover:ring-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400">
              <img src={p.url} alt={p.nom || `photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {montrer('photos') && spot !== null && d.photos?.[spot] && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={() => setSpot(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl" aria-label="Fermer" onClick={() => setSpot(null)}>✕</button>
          {spot > 0 && <button className="absolute left-3 text-white/80 hover:text-white text-3xl px-3" aria-label="Précédente" onClick={e => { e.stopPropagation(); setSpot(spot - 1); }}>‹</button>}
          <figure className="max-w-[92vw] flex flex-col items-center justify-center text-center" onClick={e => e.stopPropagation()}>
            <img src={d.photos[spot].url} alt="" className="block max-w-[92vw] max-h-[72vh] w-auto h-auto object-contain rounded-lg" />
            <figcaption className="max-h-[18vh] overflow-y-auto text-white/80 text-sm mt-2 px-2">
              {spot + 1} / {d.photos.length} · {d.photos[spot].nom}
              {(() => { const o = (d.observationsPhotos || []).find(x => x.nom === d.photos[spot].nom); return o?.constats?.length ? ` — ${o.lieu} : ${o.constats.join(' ; ')}` : ''; })()}
            </figcaption>
          </figure>
          {spot < d.photos.length - 1 && <button className="absolute right-3 text-white/80 hover:text-white text-3xl px-3" aria-label="Suivante" onClick={e => { e.stopPropagation(); setSpot(spot + 1); }}>›</button>}
        </div>
      )}

      {montrer('observations') && observations.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Ce que les photos montrent <span className="normal-case tracking-normal font-normal">— survolez ou touchez pour le détail</span></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {observations.map((o, i) => (
              <Tuile key={i} titre={`${o.n}. ${o.lieu || 'Lieu incertain'}`} texte={o.constats.join(' ; ')} />
            ))}
          </div>
        </div>
      )}

      {montrer('constats') && (d.constats?.length > 0 || d.aDemander?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4 items-start">
          {d.constats?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Constats retenus</p>
              <div className="grid gap-1.5 content-start">{d.constats.map((c, i) => <Tuile key={i} texte={c} ton="constat" />)}</div>
            </div>
          )}
          {d.aDemander?.length > 0 && (() => {
            const reponseA = (q) => (d.reponses || []).find(r => r.question === q)?.reponse || '';
            const nbReponses = (d.reponses || []).length;
            return (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">À vérifier <span className="normal-case tracking-normal font-normal">— cliquez un point pour répondre</span></p>
                <div className="grid gap-1.5">{d.aDemander.map((c, i) => <Question key={i} texte={c} reponse={reponseA(c)} onRepondre={(rep) => repondre(c, rep)} />)}</div>
                {nbReponses > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button type="button" disabled={envoi} onClick={relancerAvecReponses}
                            className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold disabled:opacity-50">
                      {envoi ? `Analyse en cours — ${etape || 'préparation'}…` : `Relancer l'analyse avec ${nbReponses} réponse${nbReponses > 1 ? 's' : ''}`}
                    </button>
                    <span className="text-[11px] text-slate-400">Les réponses font foi devant la dictée et les photos ; le relevé se reconstruit ensuite.</span>
                    {message && !envoi && <span className="text-[11px] text-red-600">{message}</span>}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {montrer('documents') && d.documents?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Documents</p>
          <ul className="space-y-1 text-sm">
            {d.documents.map((x, i) => (
              <li key={i} className="flex items-center gap-2">
                {x.url ? <a href={x.url} className="text-orange-700 hover:underline truncate">{x.nom}</a> : <span className="text-slate-500 truncate">{x.nom}</span>}
                <span className="text-xs text-slate-400 shrink-0">{mo(x.taille)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {montrer('transcription') && d.transcription && (
        <div className="mb-4">
          <button onClick={() => setVoirTranscription(v => !v)} className="text-sm font-semibold text-slate-700 hover:text-orange-600">
            {voirTranscription ? '▾' : '▸'} Transcription de la dictée ({d.transcription.split(/\s+/).length} mots)
          </button>
          {voirTranscription && <p className="mt-2 text-xs text-slate-500 leading-relaxed whitespace-pre-wrap">{d.transcription}</p>}
        </div>
      )}

      {/* ── Compléter le dossier ── */}
      {montrer('completer') && (
      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Compléter le dossier</p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={envoi}
                  className="text-xs font-semibold border border-slate-300 hover:border-orange-400 rounded-lg px-3 py-2 disabled:opacity-50">
            Ajouter des pièces…
          </button>
          <input ref={inputRef} type="file" multiple className="hidden" accept="audio/*,video/*,image/*,application/pdf"
                 onChange={e => { setAjouts(a => [...a, ...Array.from(e.target.files)]); e.target.value = ''; }} />
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={reanalyser} onChange={e => setReanalyser(e.target.checked)} disabled={envoi}
                   className="rounded border-slate-300 text-orange-600 focus:ring-orange-400" />
            Relancer l'analyse sur l'ensemble des pièces
          </label>
          <button type="button" onClick={ajouter} disabled={envoi || (!ajouts.length && !reanalyser)}
                  className="ml-auto text-xs font-semibold bg-orange-600 hover:bg-orange-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg px-3 py-2">
            {reanalyser ? 'Ajouter et réanalyser' : 'Conserver au dossier'}
          </button>
        </div>
        {ajouts.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {ajouts.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1 text-xs text-slate-600">
                {f.name.length > 30 ? f.name.slice(0, 28) + '…' : f.name}
                <button onClick={() => setAjouts(l => l.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500">✕</button>
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-400 mt-2">
          Dictées, photos, plans ou diagnostics en PDF. Sans relance, les pièces sont conservées et listées ici ;
          avec, l'analyse repart sur tout le dossier — anciennes pièces comprises — et remplace la proposition.
        </p>
        {envoi && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3" aria-live="polite">
            <div className="flex items-center gap-3">
              <p className="text-sm font-medium text-slate-800 truncate flex-1">{etape || 'En cours'}…</p>
              <span className="text-xs text-slate-400">{duree(ecoule)}</span>
              <span className="text-sm font-semibold tabular-nums text-slate-700">{Math.round(pct)} %</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full rounded-full barre-anim transition-[width] duration-500 ease-out" style={{ width: `${Math.max(2, pct)}%` }} />
            </div>
          </div>
        )}
        {message && <p className="text-sm text-slate-600 mt-2">{message}</p>}
      </div>
      )}
    </div>
  );
}
